// Client-side first-page thumbnail for a just-dropped file - used where a
// preview is wanted before/instead of asking a server to render one (see
// app/web/routes/landing.jss's anonymous demo upload, where privacy rules
// out having decipher rasterize an anonymous visitor's document). pdfjs-dist
// is dynamically imported so it only loads once a file is actually dropped,
// never as part of the module's own bundle.
//
// Returns a data URL, or null if the file isn't a PDF/image or rendering
// fails - callers treat null as "no preview available", the same as a
// failed server fetch would have been.
export async function renderFirstPageThumbnail(file, { width = 400 } = {}) {
  if (!file) return null;

  if (file.type === 'application/pdf') {
    return renderPdfFirstPage(file, width);
  }
  if (file.type.startsWith('image/')) {
    return fileToDataUrl(file);
  }
  return null;
}

async function renderPdfFirstPage(file, width) {
  try {
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url
    ).toString();

    const data = await file.arrayBuffer();
    // Without this, pdf.js can't locate its JBIG2/OpenJPEG wasm decoders (its
    // own default is a bare "wasm" relative path, which only resolves under
    // a bundler that knows to copy pdfjs-dist's own wasm/ folder there - Vite
    // doesn't, and a plain `new URL('pdfjs-dist/wasm/', import.meta.url)`
    // can't fix that either: Vite's static-asset analysis needs one literal
    // file per reference, not a whole directory pdf.js appends filenames to
    // at runtime) and SILENTLY drops any image XObject that needs one,
    // leaving that layer of the page blank rather than throwing. Many
    // scanner/MFP "compact PDF" profiles encode a scan's bilevel text/
    // line-art layer as JBIG2 specifically (it's far smaller than CCITT for
    // that content), so this isn't a rare format - it's what silently
    // produced an apparently-corrupt-looking thumbnail for an otherwise
    // perfectly good scan. /pdfjs-wasm/ is copied verbatim into the built
    // client by app/web's vite.config.js (viteStaticCopy) for exactly this -
    // an absolute, unhashed path pdf.js can safely append its own filenames
    // to (`${wasmUrl}jbig2.wasm`, etc.), the same contract its own default
    // "wasm" value assumes.
    const pdf = await pdfjsLib.getDocument({ data, wasmUrl: '/pdfjs-wasm/' }).promise;
    const page = await pdf.getPage(1);

    const unscaledViewport = page.getViewport({ scale: 1 });
    const scale = width / unscaledViewport.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const context = canvas.getContext('2d');

    await page.render({ canvasContext: context, viewport }).promise;
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch (error) {
    console.error('Failed to render PDF thumbnail:', error);
    return null;
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}
